import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, Mail, MessageSquare, CalendarCheck } from "lucide-react";
import { canonical, faqPageSchema } from "@/lib/seo";

export const FAQ = [
  {
    q: "How do I cancel a reservation at Malak BnB Resort?",
    a: "Send an email to infomalakbnb@gmail.com or a text message to 1-289-929-8055 with your name and arrival date. Malak BnB Resort accepts cancellation requests by email or text only, and the team replies in writing to confirm the cancellation and any refund amount that applies.",
  },
  {
    q: "When can I cancel for a full refund at Malak BnB Resort?",
    a: "Cancel 7 days or more before your arrival time and Malak BnB Resort issues a full refund. Inside that window the reservation moves to the partial refund tier, so guests with flexible plans are encouraged to confirm dates early and cancel as soon as plans change.",
  },
  {
    q: "What happens if I cancel within 72 hours of arrival?",
    a: "A cancellation made 72 hours or more before your arrival time, but less than 7 days out, is refunded at 50% of the total reservation rate before tax. Malak BnB Resort confirms the refund amount in writing when it acknowledges your cancellation request.",
  },
  {
    q: "Is there a refund within 48 hours of arrival at Malak BnB Resort?",
    a: "No. Malak BnB Resort does not issue a refund for cancellations made less than 48 hours before the arrival time, and no-shows are treated the same way. The room is held for you and cannot be re-let at that point, so the full reservation amount stands.",
  },
  {
    q: "How does payment work at Malak BnB Resort?",
    a: "Malak BnB Resort takes reservation requests, not instant bookings. The owner reviews your dates, confirms availability, then sends payment instructions directly. Payment is accepted through Clover, debit, or e-transfer, and a $50.00 deposit covers incidentals.",
  },
];

export const Route = createFileRoute("/terms-and-cancellation-policy")({
  head: () => ({
    meta: [
      { title: "Cancellation Policy - Malak BnB Resort, Crystal Beach" },
      { name: "description", content: "Cancellation and refund terms at Malak BnB Resort in Crystal Beach, Ontario. Full refund 7 days out, 50% at 72 hours, no refund inside 48 hours." },
      { property: "og:title", content: "Terms and Cancellation Policy - Malak BnB Resort" },
      { property: "og:description", content: "Cancellation and refund terms, how to cancel by email or text, and how payment works at Malak BnB Resort in Crystal Beach, Ontario." },
      { property: "og:url", content: "https://malakbnbresort.ca/terms-and-cancellation-policy" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://malakbnbresort.ca/images/hero-exterior.webp" },
      { name: "twitter:image", content: "https://malakbnbresort.ca/images/hero-exterior.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
    ],
    links: [canonical("/terms-and-cancellation-policy")],
  }),
  component: TermsPage,
});

const TIERS = [
  {
    window: "7 days or more before arrival",
    refund: "Full refund",
    note: "Cancel a week ahead and the whole reservation amount comes back to you.",
  },
  {
    window: "72 hours to 7 days before arrival",
    refund: "50% of the total reservation rate, before tax",
    note: "Half the reservation rate is refunded. Taxes are calculated on the refunded portion.",
  },
  {
    window: "Less than 48 hours before arrival",
    refund: "No refund",
    note: "The room is held for you at that point and cannot be offered to another guest.",
  },
];

function TermsPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[45vh] min-h-[300px] overflow-hidden">
        <img
          src="/images/hero-exterior.webp"
          alt="Exterior of Malak BnB Resort in Crystal Beach, Ontario"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-14 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Malak BnB Resort</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-3xl">
              Terms and Cancellation Policy
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-5 text-muted-foreground leading-relaxed">
          <p className="text-2xl font-heading text-foreground leading-snug">
            Plans change. Here is exactly what happens when yours do.
          </p>
          <p>
            Malak BnB Resort is a small family-run property at 264 Ridge Road South in Crystal Beach, Ontario. Every reservation is reviewed and confirmed by the owner, so a cancelled room close to the arrival date is rarely filled again in time.
          </p>
          <p>
            That is the reason for the terms below. Read them before you submit a reservation request, and get in touch if anything is unclear.
          </p>
        </div>
      </section>

      {/* Cancellation tiers */}
      <section className="pb-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-heading text-foreground mb-4">Cancellation and Refund Terms</h2>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Refunds at Malak BnB Resort depend on how far ahead of your arrival time you cancel. Full refund at 7 days, 50% of the total reservation rate before tax at 72 hours, and no refund inside 48 hours.
          </p>

          <div className="overflow-hidden rounded-2xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-card">
                <tr>
                  <th className="px-5 py-4 font-semibold text-foreground">When you cancel</th>
                  <th className="px-5 py-4 font-semibold text-foreground">Refund</th>
                </tr>
              </thead>
              <tbody>
                {TIERS.map((t) => (
                  <tr key={t.window} className="border-t border-border align-top">
                    <td className="px-5 py-4 text-foreground font-medium">{t.window}</td>
                    <td className="px-5 py-4">
                      <span className="text-foreground">{t.refund}</span>
                      <span className="block text-muted-foreground mt-1 leading-relaxed">{t.note}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-muted-foreground leading-relaxed mt-6 text-sm">
            All timings are measured against the arrival time on your confirmed reservation. Refunds are processed back through the same method used for payment, usually within five business days of the written cancellation confirmation.
          </p>
        </div>
      </section>

      {/* How to cancel */}
      <section className="py-16 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-heading text-foreground mb-4">How to Cancel or Change a Reservation</h2>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Malak BnB Resort accepts cancellation and change requests by email or text message. A request is only valid once the property replies to confirm it, so keep that reply for your records.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl bg-background p-5 border border-border">
              <Mail className="h-5 w-5 text-primary mb-3" />
              <p className="font-semibold text-foreground text-sm mb-1">Email</p>
              <a href="mailto:infomalakbnb@gmail.com" className="text-sm text-primary hover:underline break-all">
                infomalakbnb@gmail.com
              </a>
            </div>
            <div className="rounded-xl bg-background p-5 border border-border">
              <MessageSquare className="h-5 w-5 text-primary mb-3" />
              <p className="font-semibold text-foreground text-sm mb-1">Text message</p>
              <a href="sms:+12899298055" className="text-sm text-primary hover:underline">
                1-289-929-8055
              </a>
            </div>
            <div className="rounded-xl bg-background p-5 border border-border">
              <CalendarCheck className="h-5 w-5 text-primary mb-3" />
              <p className="font-semibold text-foreground text-sm mb-1">Written confirmation</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                The property replies in writing with the refund amount that applies.
              </p>
            </div>
          </div>
          <p className="text-muted-foreground leading-relaxed mt-8 text-sm">
            Include your full name, room type, and arrival date so the team can find your reservation quickly. Date changes are treated as a new request and depend on availability. If new dates cannot be found, the cancellation terms above apply.
          </p>
        </div>
      </section>

      {/* No-shows and early departure */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-8">
          <div>
            <h2 className="text-2xl font-heading text-foreground mb-4">No-Shows and Early Departure</h2>
            <p className="text-muted-foreground leading-relaxed">
              A guest who does not arrive and has not cancelled is treated as a cancellation inside 48 hours, with no refund. If you leave earlier than your confirmed check-out date, the remaining nights are not refunded. Let the property know as soon as you can either way, since it helps the family plan breakfast and housekeeping.
            </p>
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground mb-4">Booking and Payment Terms</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Malak BnB Resort takes direct reservation requests. Submitting the form is not an instant booking and no card is charged at that moment. The owner reviews your dates, confirms the room is available, and only then sends payment instructions.
              </p>
              <p>
                Payment is accepted through Clover, debit, or e-transfer. A $50.00 deposit covers incidentals and is returned after check-out when no charges apply. Rates start from C$345 per night and vary by room and season.
              </p>
              <p>
                Check the{" "}
                <Link to="/reservation-details" className="text-primary font-semibold hover:underline">
                  full booking process
                </Link>{" "}
                if you want to see each step before you request dates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-heading text-foreground mb-8">Cancellation Policy Questions</h2>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-12">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center flex flex-wrap justify-center gap-x-8 gap-y-3">
          <Link to="/rooms" className="text-primary font-semibold text-sm hover:underline">
            View rooms and rates →
          </Link>
          <Link to="/faq" className="text-primary font-semibold text-sm hover:underline">
            Read the full FAQ →
          </Link>
          <Link to="/contact" className="text-primary font-semibold text-sm hover:underline">
            Contact the property →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Ready when you are
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Check availability and submit a reservation request at Malak BnB Resort. The family reviews every request personally and confirms your dates before any payment.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request to Book
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
